import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Typography, Grid, Chip, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, TextField
} from '@mui/material';
import { PlayArrow, Download, Refresh } from '@mui/icons-material';
import { scanPenTest, getPenTestScans, getPenTestScanDetails, downloadPenTestReport } from '../../services/api';

function PenTestScanner() {
  const [profile, setProfile] = useState('quick');
  const [targetUrl, setTargetUrl] = useState('https://monitoring.dexios.cloud');
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    loadScans();
    const interval = setInterval(loadScans, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadScans = async () => {
    try {
      const response = await getPenTestScans();
      setScans(response.scans || []);
    } catch (error) {
      console.error('Failed to load scans:', error);
    }
  };

  const startScan = async () => {
    if (!targetUrl) {
      alert('Bitte geben Sie eine Ziel-URL ein');
      return;
    }
    setScanning(true);
    try {
      await scanPenTest({
        profile,
        target_url: targetUrl,
        target_name: new URL(targetUrl).hostname
      });
      await loadScans();
      alert('Penetration Test gestartet!');
    } catch (error) {
      alert('Fehler beim Starten: ' + error.message);
    } finally {
      setScanning(false);
    }
  };

  const viewDetails = async (scanId) => {
    setLoading(true);
    try {
      const details = await getPenTestScanDetails(scanId);
      setSelectedScan(details);
    } catch (error) {
      alert('Fehler beim Laden der Details');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (scanId) => {
    try {
      await downloadPenTestReport(scanId);
    } catch (error) {
      alert('Fehler beim Download: ' + error.message);
    }
  };

  const getRiskColor = (risk) => {
    const colors = {
      critical: 'error',
      high: 'warning',
      medium: 'info',
      low: 'success',
      informational: 'default'
    };
    return colors[risk] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      running: 'primary',
      failed: 'error',
      pending: 'warning'
    };
    return colors[status] || 'default';
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Neuen Penetration Test starten
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Ziel URL"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="https://example.com"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Test Profil</InputLabel>
                    <Select value={profile} onChange={(e) => setProfile(e.target.value)}>
                      <MenuItem value="quick">Quick Scan (5-10 Min)</MenuItem>
                      <MenuItem value="standard">Standard Scan (30-60 Min)</MenuItem>
                      <MenuItem value="full">Full Scan (2+ Stunden)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={scanning ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                    onClick={startScan}
                    disabled={scanning}
                    style={{ height: '56px' }}
                  >
                    {scanning ? 'Test läuft...' : 'Test starten'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Test Historie</Typography>
                <Button startIcon={<Refresh />} onClick={loadScans}>Aktualisieren</Button>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Ziel</TableCell>
                      <TableCell>Profil</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Gestartet</TableCell>
                      <TableCell align="center">Kritisch</TableCell>
                      <TableCell align="center">Hoch</TableCell>
                      <TableCell align="center">Mittel</TableCell>
                      <TableCell align="center">Niedrig</TableCell>
                      <TableCell align="center">Gesamt</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scans.map((scan) => (
                      <TableRow key={scan.id}>
                        <TableCell>{scan.id}</TableCell>
                        <TableCell>{scan.target_name || scan.target_url}</TableCell>
                        <TableCell>{scan.profile.toUpperCase()}</TableCell>
                        <TableCell>
                          <Chip label={scan.status} color={getStatusColor(scan.status)} size="small" />
                        </TableCell>
                        <TableCell>{new Date(scan.created_at).toLocaleString('de-DE')}</TableCell>
                        <TableCell align="center">
                          <Chip label={scan.critical_count} color="error" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={scan.high_count} color="warning" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={scan.medium_count} color="info" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={scan.low_count} color="success" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <strong>{scan.total_issues}</strong>
                        </TableCell>
                        <TableCell>
                          {scan.security_score !== null && scan.security_score !== undefined && (
                            <Chip
                              label={`${scan.security_score.toFixed(1)}/100`}
                              color={scan.security_score > 80 ? 'success' : scan.security_score > 60 ? 'warning' : 'error'}
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => viewDetails(scan.id)}>Details</Button>
                          {scan.status === 'completed' && (
                            <Button size="small" startIcon={<Download />} onClick={() => downloadPDF(scan.id)}>
                              PDF
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {selectedScan && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Test Details - ID: {selectedScan.id}
                </Typography>

                {loading ? (
                  <CircularProgress />
                ) : (
                  <TableContainer component={Paper} style={{ marginTop: '16px' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Risk</TableCell>
                          <TableCell>Kategorie</TableCell>
                          <TableCell>Titel</TableCell>
                          <TableCell>URL</TableCell>
                          <TableCell>Parameter</TableCell>
                          <TableCell>Confidence</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedScan.findings || []).map((finding) => (
                          <TableRow key={finding.id}>
                            <TableCell>
                              <Chip
                                label={finding.risk_level}
                                color={getRiskColor(finding.risk_level)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{finding.category.replace('_', ' ')}</TableCell>
                            <TableCell>{finding.title}</TableCell>
                            <TableCell style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {finding.url}
                            </TableCell>
                            <TableCell>{finding.parameter || 'N/A'}</TableCell>
                            <TableCell>{finding.confidence || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default PenTestScanner;
